import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { setReadingLevel as setReadingLevelApi } from "../lib/copyAdaptApi";
import { useAuth } from "./AuthContext";

/**
 * ReadingLevelContext — global "how should copy be pitched to me" preference.
 *
 * Value: 'everyday' | 'founder' | 'insider'
 * Persisted on the user document (`reading_level`) so it follows the founder
 * across devices. Falls back to localStorage for anonymous sessions and
 * seeds from the user doc when they log in.
 */
const LS_KEY = "cpg.readingLevel";
const DEFAULT_LEVEL = "insider";

const ReadingLevelContext = createContext({
  level: DEFAULT_LEVEL,
  setLevel: () => {},
  isCustom: false,
});

export function ReadingLevelProvider({ children }) {
  const { user } = useAuth() || {};
  const [level, setLevelState] = useState(DEFAULT_LEVEL);
  const [hydrated, setHydrated] = useState(false);

  // First render: pull from localStorage. Fires once — user override
  // wins over server default until we hear from the API.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(LS_KEY);
      if (stored) setLevelState(stored);
    } catch (_) { /* private mode or SSR — ignore */ }
    setHydrated(true);
  }, []);

  // When the auth user resolves, prefer the server-persisted preference
  // (unless the user changed the level in this session already).
  useEffect(() => {
    if (!hydrated) return;
    const serverLevel = user?.reading_level;
    if (serverLevel && !window.localStorage.getItem(LS_KEY)) {
      setLevelState(serverLevel);
    }
  }, [user?.reading_level, hydrated]);

  const setLevel = useCallback(async (next) => {
    setLevelState(next);
    try { window.localStorage.setItem(LS_KEY, next); } catch (_) {}
    if (user?.user_id) {
      try { await setReadingLevelApi(next); }
      catch (_) { /* preference is best-effort */ }
    }
  }, [user?.user_id]);

  return (
    <ReadingLevelContext.Provider
      value={{ level, setLevel, isCustom: level !== "insider" }}
    >
      {children}
    </ReadingLevelContext.Provider>
  );
}

export function useReadingLevel() {
  return useContext(ReadingLevelContext);
}
